import { useState, useMemo } from 'react';
import { useDebug, DebugEvent, EventCategory, ALL_CATEGORIES, ALL_SOURCES, ErrorAggregate, TimingStats } from '../context/DebugContext';
import './DebugPanel.css';

type TabType = 'events' | 'errors' | 'timing';

function formatTime(timestamp: number): string {
  const date = new Date(timestamp);
  return date.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }) + '.' + String(date.getMilliseconds()).padStart(3, '0');
}

function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  if (diff < 60000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return new Date(timestamp).toLocaleDateString();
}

function getCategoryIcon(category: EventCategory): string {
  switch (category) {
    case 'auth': return '🔐';
    case 'navigation': return '🧭';
    case 'api': return '📡';
    case 'error': return '❌';
    case 'info': return 'ℹ️';
    case 'lifecycle': return '🔄';
    case 'execution': return '⚡';
    case 'attestation': return '✅';
    case 'circuit-action': return '🔧';
    case 'timing': return '⏱️';
    default: return '•';
  }
}

function getSourceColor(source: DebugEvent['source']): string {
  switch (source) {
    case 'sdk': return '#4488ff';
    case 'console': return '#44ff88';
    case 'edge': return '#ff8844';
    case 'mobile': return '#ff44ff';
    case 'lattice': return '#ffff44';
    case 'circuit': return '#44ffff';
    case 'witness': return '#ff88ff';
    default: return '#888888';
  }
}

function getSeverityColor(severity?: string): string {
  switch (severity) {
    case 'error': return '#ef4444';
    case 'warning': return '#f59e0b';
    case 'info': return '#3b82f6';
    case 'debug': return '#6b7280';
    default: return '#888888';
  }
}

// Error aggregate row component
function ErrorAggregateRow({ error }: { error: ErrorAggregate }) {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className="error-aggregate" onClick={() => setExpanded(!expanded)}>
      <div className="error-aggregate-header">
        <span className="error-count">{error.count}x</span>
        <span className="error-message">{error.message}</span>
        <span className="error-time">{formatRelativeTime(error.lastSeen)}</span>
      </div>
      {expanded && error.sampleStack && (
        <div className="error-stack">
          <pre>{error.sampleStack}</pre>
        </div>
      )}
    </div>
  );
}

// Timing stats row with bar visualization
function TimingStatsRow({ stats }: { stats: TimingStats }) {
  // Calculate bar width based on avg time (max 100% at 5000ms)
  const barWidth = Math.min((stats.avgMs / 5000) * 100, 100);
  const barColor = stats.avgMs > 2000 ? '#ef4444' : stats.avgMs > 500 ? '#f59e0b' : '#10b981';
  
  return (
    <div className="timing-row">
      <div className="timing-info">
        <span className="timing-operation">{stats.operation}</span>
        <span className="timing-count">{stats.count} calls</span>
      </div>
      <div className="timing-stats">
        <span className="timing-avg">{stats.avgMs}ms avg</span>
        <span className="timing-range">{stats.minMs}–{stats.maxMs}ms</span>
        <span className="timing-p95">P95: {stats.p95Ms}ms</span>
      </div>
      <div className="timing-bar-container">
        <div 
          className="timing-bar" 
          style={{ width: `${barWidth}%`, backgroundColor: barColor }}
        />
      </div>
    </div>
  );
}

export default function DebugPanel() {
  const {
    state,
    filteredEvents,
    clearEvents,
    toggleVisible,
    toggleSource,
    toggleCategory,
    exportEvents,
    errorAggregates,
    timingStats,
  } = useDebug();
  
  const [showFilters, setShowFilters] = useState(true);
  const [isLive, setIsLive] = useState(true);
  const [activeTab, setActiveTab] = useState<TabType>('events');

  // Count errors for badge
  const errorCount = useMemo(() => 
    errorAggregates.reduce((sum, e) => sum + e.count, 0),
    [errorAggregates]
  );

  if (!state.isVisible) return null;
  
  const handleExport = () => {
    const data = exportEvents();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `debug-trace-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="debug-panel">
      <div className="debug-header">
        <div className="debug-title">
          <span className="debug-icon">🔧</span>
          Debug Analytics
          {isLive && <span className="live-indicator">● LIVE</span>}
        </div>
        <div className="debug-actions">
          <button 
            className={`debug-btn ${isLive ? 'active' : ''}`} 
            onClick={() => setIsLive(!isLive)} 
            title={isLive ? 'Pause' : 'Resume'}
          >
            {isLive ? '⏸' : '▶'}
          </button>
          <button 
            className="debug-btn" 
            onClick={() => setShowFilters(!showFilters)} 
            title="Toggle filters"
          >
            🔍
          </button>
          <button className="debug-btn" onClick={handleExport} title="Export events">
            📥
          </button>
          <button className="debug-btn" onClick={clearEvents} title="Clear events">
            🗑️
          </button>
          <button className="debug-btn" onClick={toggleVisible} title="Close (Ctrl+Shift+D)">
            ✕
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="debug-tabs">
        <button 
          className={`debug-tab ${activeTab === 'events' ? 'active' : ''}`}
          onClick={() => setActiveTab('events')}
        >
          Events <span className="tab-count">{filteredEvents.length}</span>
        </button>
        <button 
          className={`debug-tab ${activeTab === 'errors' ? 'active' : ''}`}
          onClick={() => setActiveTab('errors')}
        >
          Errors {errorCount > 0 && <span className="tab-count error">{errorCount}</span>}
        </button>
        <button 
          className={`debug-tab ${activeTab === 'timing' ? 'active' : ''}`}
          onClick={() => setActiveTab('timing')}
        >
          Timing <span className="tab-count">{timingStats.length}</span>
        </button>
      </div>

      {showFilters && activeTab === 'events' && (
        <div className="debug-filters">
          <div className="filter-group">
            <span className="filter-label">Source:</span>
            {ALL_SOURCES.map((source) => (
              <button
                key={source}
                className={`filter-btn ${state.filter.sources.has(source) ? 'active' : ''}`}
                style={{ borderColor: getSourceColor(source) }}
                onClick={() => toggleSource(source)}
              >
                {source}
              </button>
            ))}
          </div>
          <div className="filter-group">
            <span className="filter-label">Category:</span>
            {ALL_CATEGORIES.map((category) => (
              <button
                key={category}
                className={`filter-btn ${state.filter.categories.has(category) ? 'active' : ''}`}
                onClick={() => toggleCategory(category)}
              >
                {getCategoryIcon(category)} {category}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Events Tab */}
      {activeTab === 'events' && (
      <div className="debug-events">
        {filteredEvents.length === 0 ? (
          <div className="debug-empty">
            No events yet. Interact with the console to see debug events.
            <div className="debug-hint">Press Ctrl+Shift+D to toggle this panel</div>
          </div>
        ) : (
          filteredEvents.map((event) => (
            <div
              key={event.id}
              className={`debug-event debug-event-${event.category} debug-event-${event.severity || 'info'}`}
            >
              <div className="event-header">
                <span className="event-time">{formatTime(event.timestamp)}</span>
                <span
                  className="event-source"
                  style={{ color: getSourceColor(event.source) }}
                >
                  [{event.source}]
                </span>
                <span className="event-category">{getCategoryIcon(event.category)}</span>
                {event.severity && event.severity !== 'info' && (
                  <span 
                    className="event-severity"
                    style={{ color: getSeverityColor(event.severity) }}
                  >
                    {event.severity.toUpperCase()}
                  </span>
                )}
                {event.duration && (
                  <span className="event-duration">{event.duration}ms</span>
                )}
                {event.correlationId && (
                  <span className="event-correlation" title="Correlation ID">
                    🔗{event.correlationId.slice(0, 8)}
                  </span>
                )}
              </div>
              <div className="event-message">{event.message}</div>
              {event.data && Object.keys(event.data).length > 0 && (
                <details className="event-data">
                  <summary>Data</summary>
                  <pre>{JSON.stringify(event.data, null, 2)}</pre>
                </details>
              )}
            </div>
          ))
        )}
      </div>
      )}

      {/* Errors Tab */}
      {activeTab === 'errors' && (
        <div className="debug-errors">
          {errorAggregates.length === 0 ? (
            <div className="debug-empty">
              No errors recorded.
              <div className="debug-hint">Errors are grouped by stack trace hash</div>
            </div>
          ) : (
            <>
              <div className="error-summary">
                {errorAggregates.length} unique errors, {errorCount} total occurrences
              </div>
              {errorAggregates.map((error) => (
                <ErrorAggregateRow key={error.stackHash} error={error} />
              ))}
            </>
          )}
        </div>
      )}

      {/* Timing Tab */}
      {activeTab === 'timing' && (
        <div className="debug-timing">
          {timingStats.length === 0 ? (
            <div className="debug-empty">
              No timing data yet.
              <div className="debug-hint">API calls and operations will appear here</div>
            </div>
          ) : (
            <>
              <div className="timing-legend">
                <span className="legend-item legend-fast">Fast (&lt;500ms)</span>
                <span className="legend-item legend-medium">Medium (500-2000ms)</span>
                <span className="legend-item legend-slow">Slow (&gt;2000ms)</span>
              </div>
              {timingStats.map((stats) => (
                <TimingStatsRow key={stats.operation} stats={stats} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
